/**
 * PublicationsView — editorial root listing per workspace.
 *
 * S-063 / FEAT-006 (Phase 5). Replaces the AdminStubs.PublicationsView
 * placeholder. Reachable from Settings → Publications.
 *
 * What it does:
 *   - Workspace selector (defaults to first agencyclient in tenant).
 *     Publications are per-agencyclient and the create / list endpoints
 *     filter by `?agencyclient_id=`. Selection persists via session
 *     storage so flipping between Team / Publications keeps context.
 *   - Lists publications via GET /publications?agencyclient_id=N. Each
 *     row shows: Name, Slug, Voice (link → /settings/voice/:id), Brand
 *     (link → /settings/brand/:id), Created at, Edit + Delete actions.
 *     Voice/brand links use the publication id (not the underlying
 *     voice/brand id) because the editor routes are keyed on
 *     :publicationId.
 *   - Click row or Edit → drawer with editable fields: name, slug,
 *     voice_profile_id (dropdown of voice profiles in this workspace),
 *     brand_identity_id (dropdown of brand identities in this workspace).
 *     PATCH /publications/:id; 409 on slug collision surfaces inline.
 *   - Create modal: Name (text), Slug (auto-derived from name, editable),
 *     Voice profile (dropdown of existing OR "+ Create new default" inline
 *     creation), Brand identity (same). On submit, creates missing
 *     voice/brand first, then POSTs the publication.
 *   - Delete: hard delete via DELETE /publications/:id. Phase 5 mirrors
 *     Phase 4's workspace delete pattern — confirm modal warns the row
 *     is destroyed (no soft-delete column exists on publications).
 *
 * Edge cases handled:
 *   - Empty workspace (no publications yet) — empty state with the
 *     "+ New publication" CTA. (The wizard's default publication usually
 *     pre-fills this list, so this only fires for workspaces created
 *     post-onboarding without a publication, or after deletion.)
 *   - Slug collision — 409 surfaced inline on the slug field of both
 *     create modal and edit drawer.
 *   - Missing voice/brand catalogs — if neither voice profiles nor brand
 *     identities exist (theoretical — onboarding seeds both), the
 *     dropdowns offer only the "+ Create new default" option.
 *   - No workspaces at all — render the same empty state TeamView uses.
 *
 * RBAC:
 *   - Reachable only via the role-gated Settings rail (org:admin).
 *   - The API enforces requireAdmin server-side on POST/PATCH/DELETE
 *     (any 403 surfaces with the friendly ApiError message).
 */

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError, useApi, useApiMutation, useApiQuery } from "@/lib/api";
import { useActiveWorkspace, useEnsureActiveWorkspace } from "@/lib/useActiveWorkspace";
import { REVIEW_STAGES } from "@shared/fiction-phases";
import {
  LLM_PROVIDERS,
  hasActiveKeyFor,
  providerLabel,
  type ApiKeyLike,
} from "@/lib/llm-providers";
import { TONES } from "@/lib/tones";
// Consolidation (option 3): shared admin modal/drawer shell. Replaces the
// local ModalShell/DrawerShell that used to live at the bottom of this file.
import { ModalShell, DrawerShell } from "@/components/SettingsModalShell";
import { SectionHeader } from "@/components/SectionHeader";
import { useSettingsWorkspaces } from "@/lib/useSettingsWorkspaces";

const ALL_REVIEW_STAGE_VALUES = REVIEW_STAGES.map((s) => s.value);

interface Agencyclient {
  id: number;
  name: string;
  slug: string;
  reviewStages?: string[];
  review_stages?: string[];
}

interface Publication {
  id: number;
  tenantId?: number;
  agencyclientId?: number;
  agencyclient_id?: number;
  slug: string;
  name: string;
  description?: string | null;
  voiceProfileId?: number | null;
  voice_profile_id?: number | null;
  brandIdentityId?: number | null;
  brand_identity_id?: number | null;
  defaultLanguage?: string;
  default_language?: string;
  // BUG-028 — falls back into piece creation when the Create Piece form
  // doesn't override it. Always one of TONES; DB default is "professional".
  defaultTone?: string;
  default_tone?: string;
  // FEAT-015 — publication-level Spanish country variant (null = not set).
  spanishCountry?: string | null;
  spanish_country?: string | null;
  // FEAT-025 — per-publication review gate override. null = inherit workspace default.
  reviewStages?: string[] | null;
  review_stages?: string[] | null;
  // FEAT-013 — provider preference. null = inherit workspace → priority cascade.
  preferredLlmProvider?: string | null;
  preferred_llm_provider?: string | null;
  createdAt?: string;
  created_at?: string;
}

function preferredProviderOf(p: Publication): string | null {
  return p.preferredLlmProvider ?? p.preferred_llm_provider ?? null;
}

function defaultToneOf(p: Publication): string {
  return p.defaultTone ?? p.default_tone ?? "professional";
}

// FEAT-015 — Spanish country dropdown options, in the spec's confirmed order
// (speaker count + commercial relevance hybrid). Neutral is the final country
// option; "(Not set)" is rendered separately as the empty value.
const SPANISH_COUNTRY_OPTIONS: ReadonlyArray<{ code: string; label: string }> = [
  { code: "MX", label: "Mexico" },
  { code: "US", label: "United States (Spanish)" },
  { code: "CO", label: "Colombia" },
  { code: "AR", label: "Argentina" },
  { code: "PE", label: "Peru" },
  { code: "VE", label: "Venezuela" },
  { code: "ES", label: "Spain" },
  { code: "CL", label: "Chile" },
  { code: "GT", label: "Guatemala" },
  { code: "EC", label: "Ecuador" },
  { code: "CU", label: "Cuba" },
  { code: "BO", label: "Bolivia" },
  { code: "DO", label: "Dominican Republic" },
  { code: "HN", label: "Honduras" },
  { code: "PY", label: "Paraguay" },
  { code: "SV", label: "El Salvador" },
  { code: "NI", label: "Nicaragua" },
  { code: "CR", label: "Costa Rica" },
  { code: "PA", label: "Panama" },
  { code: "PR", label: "Puerto Rico" },
  { code: "UY", label: "Uruguay" },
  { code: "neutral", label: "Neutral Spanish" },
];

interface VoiceProfile {
  id: number;
  name: string;
}

interface BrandIdentity {
  id: number;
  name: string;
}

interface PublicationsResponse {
  data: Publication[];
  meta: { total: number; limit: number; offset: number };
}

interface VoiceProfilesResponse {
  data: VoiceProfile[];
  meta: { total: number; limit: number; offset: number };
}

interface BrandIdentitiesResponse {
  data: BrandIdentity[];
  meta: { total: number; limit: number; offset: number };
}

const PUBLICATIONS_WS_KEY = "ccm:publications:active_ws";

// ---------- Helpers ----------

function slugify(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

function isValidSlug(s: string): boolean {
  if (s.length < 1 || s.length > 80) return false;
  return /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/.test(s);
}

function fmtDate(iso: string | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
}

function voiceIdOf(p: Publication): number | null {
  return p.voiceProfileId ?? p.voice_profile_id ?? null;
}

function brandIdOf(p: Publication): number | null {
  return p.brandIdentityId ?? p.brand_identity_id ?? null;
}

function createdAtOf(p: Publication): string | undefined {
  return p.createdAt ?? p.created_at;
}

function languageOf(p: Publication): string {
  return p.defaultLanguage ?? p.default_language ?? "en";
}

function spanishCountryOf(p: Publication): string | null {
  return p.spanishCountry ?? p.spanish_country ?? null;
}

// FEAT-025 — the per-publication override (null = inherit workspace default).
function reviewStagesOf(p: Publication): string[] | null {
  const raw = p.reviewStages ?? p.review_stages;
  return Array.isArray(raw) ? raw : null;
}

function workspaceReviewStagesOf(ac: Agencyclient | undefined): string[] {
  const raw = ac?.reviewStages ?? ac?.review_stages;
  return Array.isArray(raw) ? raw : ALL_REVIEW_STAGE_VALUES;
}

// ---------- Top-level view ----------

export function PublicationsView() {
  const { data: workspaces, isLoading: wsLoading, error: wsError } =
    useSettingsWorkspaces<Agencyclient>();

  const [selectedId, setSelectedId] = useState<number | null>(() => {
    if (typeof window === "undefined") return null;
    const raw = window.sessionStorage.getItem(PUBLICATIONS_WS_KEY);
    return raw ? Number(raw) : null;
  });

  // Persist selection so the user can flip to Team and back without
  // losing the active workspace.
  useEffect(() => {
    if (selectedId != null) {
      try {
        window.sessionStorage.setItem(
          PUBLICATIONS_WS_KEY,
          String(selectedId)
        );
      } catch {
        // sessionStorage may be unavailable in some browser configs;
        // we tolerate the loss and fall back to first-workspace default.
      }
    }
  }, [selectedId]);

  // Default to first workspace once the list loads, if no valid selection.
  useEffect(() => {
    if (workspaces && workspaces.length > 0) {
      const stillValid = workspaces.some((w) => w.id === selectedId);
      if (!stillValid) {
        setSelectedId(workspaces[0].id);
      }
    }
  }, [workspaces, selectedId]);

  if (wsLoading) {
    return (
      <main className="main settings-main">
        <div className="settings-page">
          <div className="state-loading">Loading publications…</div>
        </div>
      </main>
    );
  }

  if (wsError) {
    return (
      <main className="main settings-main">
        <div className="settings-page">
          <div className="state-error">
            {wsError instanceof ApiError && wsError.status === 403
              ? "You don't have permission to view publications."
              : (wsError.message ?? "Could not load workspaces.")}
          </div>
        </div>
      </main>
    );
  }

  if (!workspaces || workspaces.length === 0) {
    return (
      <main className="main settings-main">
        <div className="settings-page">
          <SectionHeader
            eyebrow="Settings · Publications"
            title="Publications"
            description="Editorial roots for your workspaces — voice, brand, and publish target defaults attach here."
          />
          <div className="settings-empty">
            <h2>No workspaces yet</h2>
            <p>
              Create a workspace under Settings → Workspaces before adding
              publications.
            </p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <PublicationsViewBody
      workspaces={workspaces}
      selectedId={selectedId}
      onSelect={setSelectedId}
    />
  );
}

interface BodyProps {
  workspaces: Agencyclient[];
  selectedId: number | null;
  onSelect: (id: number) => void;
}

function PublicationsViewBody({ workspaces, selectedId, onSelect }: BodyProps) {
  const qc = useQueryClient();
  const activeWs = useMemo(
    () => workspaces.find((w) => w.id === selectedId) ?? workspaces[0],
    [workspaces, selectedId]
  );
  const activeId = activeWs.id;

  // S-199 — sync the JWT agencyclient_id claim to the displayed workspace on
  // load so reads (RLS scope) and mutations target it.
  useEnsureActiveWorkspace(activeId);

  // S-077 — wire the selector to the active-workspace switcher so the
  // JWT actually flips to the chosen agencyclient. Without this, the
  // selector only changes what we DISPLAY; any POST/PATCH/DELETE
  // mutation would still run against the JWT's original
  // agencyclient_id and trip RLS on cross-workspace writes.
  const { activate, isActivating, error: switchError, clearError } =
    useActiveWorkspace();

  const { data, isLoading, error } = useApiQuery<PublicationsResponse>(
    ["publications", activeId],
    `/publications?agencyclient_id=${activeId}`
  );

  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<Publication | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Publication | null>(null);
  // Phase 5 UX polish (2026-05-25): track the workspace the user is
  // SWITCHING TO so the <select> shows the new label during the
  // activate() round-trip, even though local `selectedId` only flips
  // after activation succeeds. This keeps the list rendering against
  // the current scope (no flicker) while the selector reflects intent.
  const [pendingId, setPendingId] = useState<number | null>(null);

  function invalidate() {
    void qc.invalidateQueries({ queryKey: ["publications", activeId] });
  }

  async function handleWorkspaceChange(nextId: number) {
    if (nextId === activeId) return;
    clearError();
    // Phase 5 UX polish (2026-05-25): hold local selection until
    // activate() resolves. Previously this flipped optimistically,
    // which caused the publications query (keyed on activeId) to
    // refetch under the OLD JWT and briefly return an empty list
    // before the JWT refresh propagated — visible as an empty-state
    // flash between the two workspaces. The selector reflects the
    // pending target via pendingId so the UI still feels responsive.
    setPendingId(nextId);
    try {
      await activate(nextId);
      onSelect(nextId);
    } catch {
      // useActiveWorkspace surfaces the error via switchError. No
      // revert needed: we never moved off the previous selection.
    } finally {
      setPendingId(null);
    }
  }

  return (
    <main className="main settings-main">
      <div className="settings-page">
        <SectionHeader
          eyebrow="Settings · Publications"
          title="Publications"
          description="Editorial roots for your workspaces — voice, brand, and publish target defaults attach here."
          action={
            <button
              type="button"
              className="btn-primary"
              onClick={() => setShowCreate(true)}
              disabled={isActivating}
            >
              + New publication
            </button>
          }
        />

        <div className="team-toolbar">
          <span className="team-toolbar-label">Workspace</span>
          <select
            className="settings-select"
            value={pendingId ?? activeId}
            disabled={isActivating}
            onChange={(e) => {
              void handleWorkspaceChange(Number(e.target.value));
            }}
          >
            {workspaces.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name} ({w.slug})
              </option>
            ))}
          </select>
          {isActivating && (
            <span className="team-toolbar-hint">
              Switching workspace…
            </span>
          )}
          {switchError && !isActivating && (
            <span className="settings-field-error">
              {switchError.status === 403
                ? "You don't have access to that workspace."
                : (switchError.message ?? "Could not switch workspace.")}
            </span>
          )}
        </div>

        {isLoading && (
          <div className="state-loading">Loading publications…</div>
        )}

        {error && !isLoading && (
          <div className="state-error">
            {error instanceof ApiError && error.status === 403
              ? "You don't have permission to view publications."
              : (error.message ?? "Could not load publications.")}
          </div>
        )}

        {!isLoading && !error && (data?.data?.length ?? 0) === 0 && (
          <EmptyState onCreate={() => setShowCreate(true)} />
        )}

        {!isLoading && !error && (data?.data?.length ?? 0) > 0 && (
          <PublicationsTable
            rows={data!.data}
            onEdit={setEditing}
            onDelete={setConfirmDelete}
          />
        )}
      </div>

      {showCreate && (
        <CreatePublicationModal
          agencyclientId={activeId}
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            invalidate();
            setShowCreate(false);
          }}
        />
      )}

      {editing && (
        <EditPublicationDrawer
          publication={editing}
          agencyclientId={activeId}
          onClose={() => setEditing(null)}
          onSaved={() => {
            invalidate();
            setEditing(null);
          }}
        />
      )}

      {confirmDelete && (
        <ConfirmDeleteModal
          publication={confirmDelete}
          onCancel={() => setConfirmDelete(null)}
          onDeleted={() => {
            invalidate();
            setConfirmDelete(null);
          }}
        />
      )}
    </main>
  );
}

// ---------- List + empty state ----------

interface TableProps {
  rows: Publication[];
  onEdit: (p: Publication) => void;
  onDelete: (p: Publication) => void;
}

function PublicationsTable({ rows, onEdit, onDelete }: TableProps) {
  // FEAT-013 — shared api-keys cache drives the per-row provider config check.
  const { data: apiKeys } = useApiQuery<{ data: ApiKeyLike[] }>(
    ["api-keys"],
    "/api-keys"
  );
  return (
    <table className="workspaces-table">
      <thead>
        <tr>
          <th>Name</th>
          <th>Slug</th>
          <th>Voice</th>
          <th>Brand</th>
          <th>Provider</th>
          <th>Created</th>
          <th className="ws-actions-col">Actions</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((p) => (
          <tr
            key={p.id}
            className="workspaces-row"
            onClick={() => onEdit(p)}
          >
            <td className="ws-name-cell">
              <span className="ws-name">{p.name}</span>
            </td>
            <td>
              <code className="mono ws-slug">{p.slug}</code>
            </td>
            <td onClick={(e) => e.stopPropagation()}>
              {voiceIdOf(p) != null ? (
                <Link
                  to={`/settings/voice/${voiceIdOf(p)}`}
                  className="settings-link"
                >
                  Edit voice
                </Link>
              ) : (
                <span className="pill pill-warn">Missing</span>
              )}
            </td>
            <td onClick={(e) => e.stopPropagation()}>
              {brandIdOf(p) != null ? (
                <Link
                  to={`/settings/brand/${p.id}`}
                  className="settings-link"
                >
                  Edit brand
                </Link>
              ) : (
                <span className="pill pill-warn">Missing</span>
              )}
            </td>
            <td onClick={(e) => e.stopPropagation()}>
              {(() => {
                const pref = preferredProviderOf(p);
                if (!pref) {
                  return <span className="pill pill-muted">Default</span>;
                }
                const ok = hasActiveKeyFor(apiKeys?.data, pref);
                return ok ? (
                  <span className="pill pill-ok">{providerLabel(pref)}</span>
                ) : (
                  <span className="pill pill-warn" title={`No active ${providerLabel(pref)} key`}>
                    Config issue
                  </span>
                );
              })()}
            </td>
            <td className="ws-date">{fmtDate(createdAtOf(p))}</td>
            <td className="ws-actions" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="btn-ghost"
                onClick={() => onEdit(p)}
              >
                Edit
              </button>
              <button
                type="button"
                className="btn-ghost btn-danger"
                onClick={() => onDelete(p)}
              >
                Delete
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="settings-empty">
      <h2>No publications yet</h2>
      <p>
        Add a publication so pieces in this workspace can attach to a voice
        profile and brand identity.
      </p>
      <button type="button" className="btn-primary" onClick={onCreate}>
        + New publication
      </button>
    </div>
  );
}

// ---------- Create modal ----------

interface CreateProps {
  agencyclientId: number;
  onClose: () => void;
  onCreated: () => void;
}

// Sentinel value for the "+ Create new default" dropdown choice.
const NEW_DEFAULT = "__new_default__";

interface CreateBody {
  agencyclient_id: number;
  name: string;
  slug: string;
  voice_profile_id: number;
  brand_identity_id: number;
}

function CreatePublicationModal({
  agencyclientId,
  onClose,
  onCreated,
}: CreateProps) {
  const api = useApi();
  const qc = useQueryClient();

  const { data: voices, isLoading: voicesLoading } =
    useApiQuery<VoiceProfilesResponse>(
      ["voice-profiles", agencyclientId],
      `/voice-profiles?agencyclient_id=${agencyclientId}`
    );
  const { data: brands, isLoading: brandsLoading } =
    useApiQuery<BrandIdentitiesResponse>(
      ["brand-identities", agencyclientId],
      `/brand-identities?agencyclient_id=${agencyclientId}`
    );

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugDirty, setSlugDirty] = useState(false);
  const [voiceChoice, setVoiceChoice] = useState<string>("");
  const [brandChoice, setBrandChoice] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!slugDirty) setSlug(slugify(name));
  }, [name, slugDirty]);

  // Default voice / brand choice to the first existing row (or
  // NEW_DEFAULT if none exist).
  useEffect(() => {
    if (voiceChoice === "" && voices) {
      setVoiceChoice(
        voices.data.length > 0 ? String(voices.data[0].id) : NEW_DEFAULT
      );
    }
  }, [voices, voiceChoice]);
  useEffect(() => {
    if (brandChoice === "" && brands) {
      setBrandChoice(
        brands.data.length > 0 ? String(brands.data[0].id) : NEW_DEFAULT
      );
    }
  }, [brands, brandChoice]);

  const slugError = slug.length > 0 && !isValidSlug(slug);
  const nameError = name.trim().length === 0 || name.trim().length > 120;
  const collisionError =
    serverError instanceof ApiError && serverError.status === 409;
  const validationError =
    serverError instanceof ApiError && serverError.status === 422;
  const canSubmit =
    !submitting &&
    !nameError &&
    !slugError &&
    slug.length > 0 &&
    voiceChoice !== "" &&
    brandChoice !== "";

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setServerError(null);
    try {
      // Resolve voice — either an existing id or a freshly-created default.
      let voiceId: number;
      if (voiceChoice === NEW_DEFAULT) {
        const row = await api<VoiceProfile>("/voice-profiles", {
          method: "POST",
          body: JSON.stringify({
            agencyclient_id: agencyclientId,
            name: "Default voice",
            core_identity:
              "Default voice profile — edit in Settings to capture your publication's tone, hard rules, and litmus tests.",
            hard_rules: [],
          }),
        });
        voiceId = row.id;
        void qc.invalidateQueries({
          queryKey: ["voice-profiles", agencyclientId],
        });
      } else {
        voiceId = Number(voiceChoice);
      }

      // Resolve brand — same pattern.
      let brandId: number;
      if (brandChoice === NEW_DEFAULT) {
        const row = await api<BrandIdentity>("/brand-identities", {
          method: "POST",
          body: JSON.stringify({
            agencyclient_id: agencyclientId,
            name: "Default brand",
          }),
        });
        brandId = row.id;
        void qc.invalidateQueries({
          queryKey: ["brand-identities", agencyclientId],
        });
      } else {
        brandId = Number(brandChoice);
      }

      const body: CreateBody = {
        agencyclient_id: agencyclientId,
        name: name.trim(),
        slug,
        voice_profile_id: voiceId,
        brand_identity_id: brandId,
      };
      await api<Publication>("/publications", {
        method: "POST",
        body: JSON.stringify(body),
      });
      onCreated();
    } catch (err) {
      if (err instanceof ApiError) {
        setServerError(err);
      } else {
        setServerError(
          new ApiError(0, String(err), "Could not create publication")
        );
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ModalShell onClose={onClose} eyebrow="Publications" title="New publication">
      <form className="settings-form" onSubmit={submit}>
        <label className="settings-field">
          <span className="settings-label">Name</span>
          <input
            className="settings-input"
            type="text"
            value={name}
            maxLength={120}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            placeholder="e.g. The CMO Lab Newsletter"
          />
          {nameError && name.length > 0 && (
            <span className="settings-field-error">
              Name must be 1–120 characters.
            </span>
          )}
        </label>

        <label className="settings-field">
          <span className="settings-label">
            Slug{" "}
            <span className="settings-label-hint">URL-safe identifier</span>
          </span>
          <input
            className="settings-input mono"
            type="text"
            value={slug}
            maxLength={80}
            onChange={(e) => {
              setSlug(e.target.value);
              setSlugDirty(true);
            }}
            placeholder="cmo-lab-newsletter"
          />
          {slugError && (
            <span className="settings-field-error">
              Kebab-case only: lowercase letters, digits, and hyphens.
            </span>
          )}
          {collisionError && (
            <span className="settings-field-error">
              That slug is already taken in this workspace.
            </span>
          )}
        </label>

        <label className="settings-field">
          <span className="settings-label">Voice profile</span>
          <select
            className="settings-select"
            value={voiceChoice}
            onChange={(e) => setVoiceChoice(e.target.value)}
            disabled={voicesLoading}
          >
            {voices?.data.map((v) => (
              <option key={v.id} value={String(v.id)}>
                {v.name}
              </option>
            ))}
            <option value={NEW_DEFAULT}>+ Create new default voice</option>
          </select>
          <span className="settings-label-hint" style={{ fontSize: 12 }}>
            You can edit the voice profile after creating the publication.
          </span>
        </label>

        <label className="settings-field">
          <span className="settings-label">Brand identity</span>
          <select
            className="settings-select"
            value={brandChoice}
            onChange={(e) => setBrandChoice(e.target.value)}
            disabled={brandsLoading}
          >
            {brands?.data.map((b) => (
              <option key={b.id} value={String(b.id)}>
                {b.name}
              </option>
            ))}
            <option value={NEW_DEFAULT}>+ Create new default brand</option>
          </select>
          <span className="settings-label-hint" style={{ fontSize: 12 }}>
            You can fill in colors, typography, and logo after creating.
          </span>
        </label>

        {validationError && !collisionError && (
          <div className="settings-form-error">
            {serverError?.body || "The server rejected the request."}
          </div>
        )}
        {serverError &&
          !collisionError &&
          !validationError &&
          serverError.status !== 409 && (
            <div className="settings-form-error">{serverError.message}</div>
          )}

        <div className="settings-form-actions">
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={!canSubmit}
          >
            {submitting ? "Creating…" : "Create publication"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

// ---------- Edit drawer ----------

interface EditProps {
  publication: Publication;
  agencyclientId: number;
  onClose: () => void;
  onSaved: () => void;
}

interface UpdateBody {
  name?: string;
  slug?: string;
  voice_profile_id?: number;
  brand_identity_id?: number;
  // FEAT-015 — null clears the variant ("(Not set)").
  spanish_country?: string | null;
  // FEAT-025 — null = inherit workspace default; array = explicit override.
  review_stages?: string[] | null;
  // FEAT-013 — null = inherit (workspace → priority); slug = explicit provider.
  preferred_llm_provider?: string | null;
  // BUG-028 — always one of TONES, no "inherit" option (DB column is NOT NULL).
  default_tone?: string;
}

function EditPublicationDrawer({
  publication,
  agencyclientId,
  onClose,
  onSaved,
}: EditProps) {
  const { data: voices } = useApiQuery<VoiceProfilesResponse>(
    ["voice-profiles", agencyclientId],
    `/voice-profiles?agencyclient_id=${agencyclientId}`
  );
  const { data: brands } = useApiQuery<BrandIdentitiesResponse>(
    ["brand-identities", agencyclientId],
    `/brand-identities?agencyclient_id=${agencyclientId}`
  );

  // FEAT-025 — workspace default (for the "inherit" preview). Reuses the cached
  // ["agencyclients"] query the top-level view already populated.
  const { data: workspaces } = useApiQuery<Agencyclient[]>(
    ["agencyclients"],
    "/agencyclients"
  );
  const workspaceDefault = workspaceReviewStagesOf(
    workspaces?.find((w) => w.id === agencyclientId)
  );

  const initialVoice = voiceIdOf(publication);
  const initialBrand = brandIdOf(publication);
  const initialSpanishCountry = spanishCountryOf(publication);
  const isSpanish = languageOf(publication) === "es";
  const initialReviewStages = reviewStagesOf(publication); // null = inherit

  const [name, setName] = useState(publication.name);
  const [slug, setSlug] = useState(publication.slug);
  const [voiceId, setVoiceId] = useState<number | null>(initialVoice);
  const [brandId, setBrandId] = useState<number | null>(initialBrand);
  // FEAT-015 — "" means "(Not set)" → sends null on save.
  const [spanishCountry, setSpanishCountry] = useState<string>(
    initialSpanishCountry ?? ""
  );
  // FEAT-025 — inherit toggle + the explicit-override selection.
  const [inheritReview, setInheritReview] = useState(initialReviewStages === null);
  const [reviewStages, setReviewStages] = useState<string[]>(
    initialReviewStages ?? workspaceDefault
  );

  // FEAT-013 — provider preference. "" = inherit (sends null).
  const initialProvider = preferredProviderOf(publication);
  const [provider, setProvider] = useState<string>(initialProvider ?? "");

  // BUG-028 — always a concrete value, no inherit option.
  const initialDefaultTone = defaultToneOf(publication);
  const [defaultTone, setDefaultTone] = useState<string>(initialDefaultTone);
  // Active keys (any visible scope) drive the inline no-key warning.
  const { data: apiKeys } = useApiQuery<{ data: ApiKeyLike[] }>(
    ["api-keys"],
    "/api-keys"
  );
  const providerHasNoKey =
    provider !== "" && !hasActiveKeyFor(apiKeys?.data, provider);

  const mutation = useApiMutation<Publication, UpdateBody>(
    `/publications/${publication.id}`,
    "PATCH",
    {
      onSuccess: () => onSaved(),
    }
  );

  // FEAT-025 — the value we'd send: null when inheriting, else the selection.
  const nextReviewStages = inheritReview ? null : reviewStages;
  const reviewDirty = useMemo(() => {
    if ((initialReviewStages === null) !== (nextReviewStages === null)) return true;
    if (initialReviewStages === null || nextReviewStages === null) return false;
    if (initialReviewStages.length !== nextReviewStages.length) return true;
    const a = [...initialReviewStages].sort();
    const b = [...nextReviewStages].sort();
    return a.some((v, i) => v !== b[i]);
  }, [initialReviewStages, nextReviewStages]);

  const providerDirty = provider !== (initialProvider ?? "");
  const defaultToneDirty = defaultTone !== initialDefaultTone;

  const dirty = useMemo(() => {
    return (
      name.trim() !== publication.name ||
      slug !== publication.slug ||
      voiceId !== initialVoice ||
      brandId !== initialBrand ||
      (isSpanish && spanishCountry !== (initialSpanishCountry ?? "")) ||
      reviewDirty ||
      providerDirty ||
      defaultToneDirty
    );
  }, [
    name,
    slug,
    voiceId,
    brandId,
    spanishCountry,
    isSpanish,
    publication,
    initialVoice,
    initialBrand,
    initialSpanishCountry,
    reviewDirty,
    providerDirty,
    defaultToneDirty,
  ]);

  const slugError = slug.length > 0 && !isValidSlug(slug);
  const nameError = name.trim().length === 0 || name.trim().length > 120;
  const collisionError =
    mutation.error instanceof ApiError && mutation.error.status === 409;
  const canSubmit =
    !mutation.isPending && dirty && !nameError && !slugError;

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    const body: UpdateBody = {};
    if (name.trim() !== publication.name) body.name = name.trim();
    if (slug !== publication.slug) body.slug = slug;
    if (voiceId != null && voiceId !== initialVoice) {
      body.voice_profile_id = voiceId;
    }
    if (brandId != null && brandId !== initialBrand) {
      body.brand_identity_id = brandId;
    }
    if (isSpanish && spanishCountry !== (initialSpanishCountry ?? "")) {
      // "" → null clears the variant; otherwise send the ISO code / "neutral".
      body.spanish_country = spanishCountry === "" ? null : spanishCountry;
    }
    if (reviewDirty) {
      // null = inherit workspace default; array = explicit override.
      body.review_stages = nextReviewStages;
    }
    if (providerDirty) {
      // "" = inherit → send null; otherwise the chosen provider slug.
      body.preferred_llm_provider = provider === "" ? null : provider;
    }
    if (defaultToneDirty) {
      body.default_tone = defaultTone;
    }
    mutation.mutate(body);
  }

  return (
    <DrawerShell onClose={onClose} eyebrow="Publications" title="Edit publication">
      <form className="settings-form" onSubmit={submit}>
        <label className="settings-field">
          <span className="settings-label">Name</span>
          <input
            className="settings-input"
            type="text"
            value={name}
            maxLength={120}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
          {nameError && (
            <span className="settings-field-error">
              Name must be 1–120 characters.
            </span>
          )}
        </label>

        <label className="settings-field">
          <span className="settings-label">Slug</span>
          <input
            className="settings-input mono"
            type="text"
            value={slug}
            maxLength={80}
            onChange={(e) => setSlug(e.target.value)}
          />
          {slugError && (
            <span className="settings-field-error">
              Kebab-case only: lowercase letters, digits, and hyphens.
            </span>
          )}
          {collisionError && (
            <span className="settings-field-error">
              That slug is already taken in this workspace.
            </span>
          )}
        </label>

        <label className="settings-field">
          <span className="settings-label">Voice profile</span>
          <select
            className="settings-select"
            value={voiceId != null ? String(voiceId) : ""}
            onChange={(e) =>
              setVoiceId(e.target.value ? Number(e.target.value) : null)
            }
          >
            {voices?.data.map((v) => (
              <option key={v.id} value={String(v.id)}>
                {v.name}
              </option>
            ))}
          </select>
          {voiceId != null && (
            <Link
              to={`/settings/voice/${voiceId}`}
              className="settings-label-hint"
              style={{ fontSize: 12 }}
            >
              Open voice profile editor →
            </Link>
          )}
        </label>

        <label className="settings-field">
          <span className="settings-label">Brand identity</span>
          <select
            className="settings-select"
            value={brandId != null ? String(brandId) : ""}
            onChange={(e) =>
              setBrandId(e.target.value ? Number(e.target.value) : null)
            }
          >
            {brands?.data.map((b) => (
              <option key={b.id} value={String(b.id)}>
                {b.name}
              </option>
            ))}
          </select>
          <Link
            to={`/settings/brand/${publication.id}`}
            className="settings-label-hint"
            style={{ fontSize: 12 }}
          >
            Open brand identity editor →
          </Link>
        </label>

        {/* FEAT-013 — LLM provider preference. "" = inherit workspace default. */}
        <label className="settings-field">
          <span className="settings-label">Preferred LLM provider</span>
          <select
            className="settings-select"
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
          >
            <option value="">Default (workspace setting)</option>
            {LLM_PROVIDERS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
          {providerHasNoKey && (
            <span className="settings-field-error">
              No active key for this provider. Add one in Settings → API Keys, or
              runs for this publication will fail.
            </span>
          )}
        </label>

        {/* BUG-028 — default tone for new pieces on this publication. Always
            a concrete value (no inherit option — the DB column is NOT NULL). */}
        <label className="settings-field">
          <span className="settings-label">Default tone</span>
          <select
            className="settings-select"
            value={defaultTone}
            onChange={(e) => setDefaultTone(e.target.value)}
          >
            {TONES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
          <span className="settings-label-hint" style={{ fontSize: 12 }}>
            {TONES.find((t) => t.value === defaultTone)?.desc} — pre-fills the
            Create Piece tone dropdown; can still be overridden per piece.
          </span>
        </label>

        {/* FEAT-015 — Spanish country variant. Shown only for es publications. */}
        {isSpanish && (
          <label className="settings-field">
            <span className="settings-label">Spanish country variant</span>
            <select
              className="settings-select"
              value={spanishCountry}
              onChange={(e) => setSpanishCountry(e.target.value)}
            >
              <option value="">(Not set — use global default)</option>
              {SPANISH_COUNTRY_OPTIONS.map((opt) => (
                <option key={opt.code} value={opt.code}>
                  {opt.label}
                </option>
              ))}
            </select>
            <span className="settings-label-hint" style={{ fontSize: 12 }}>
              Country-targeted Spanish reads as native to that market; Neutral
              works for pan-regional campaigns but reads as marketing
              translation to most readers.
            </span>
          </label>
        )}

        {/* FEAT-025 — per-publication review gates. */}
        <fieldset className="settings-field" style={{ border: "none", padding: 0, margin: 0 }}>
          <span className="settings-label">Review gates</span>
          <label className="settings-check-row">
            <input
              type="checkbox"
              checked={inheritReview}
              onChange={(e) => {
                const on = e.target.checked;
                setInheritReview(on);
                // Seed the editable selection from the workspace default when
                // switching to an explicit override.
                if (!on) setReviewStages(workspaceDefault);
              }}
            />
            <span>Inherit from workspace default</span>
          </label>
          <div className="settings-check-group">
            {REVIEW_STAGES.map((stage) => {
              const checked = inheritReview
                ? workspaceDefault.includes(stage.value)
                : reviewStages.includes(stage.value);
              return (
                <label key={stage.value} className="settings-check-row">
                  <input
                    type="checkbox"
                    disabled={inheritReview}
                    checked={checked}
                    onChange={(e) =>
                      setReviewStages((prev) =>
                        e.target.checked
                          ? [...prev, stage.value]
                          : prev.filter((v) => v !== stage.value)
                      )
                    }
                  />
                  <span>{stage.label}</span>
                </label>
              );
            })}
          </div>
          <span className="settings-label-hint" style={{ fontSize: 12 }}>
            The pipeline pauses for human review after each checked stage.
            Publishing always requires explicit approval regardless of these
            settings.
          </span>
        </fieldset>

        {mutation.error && !collisionError && (
          <div className="settings-form-error">
            {mutation.error.body || mutation.error.message}
          </div>
        )}

        <div className="settings-form-actions">
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={!canSubmit}
          >
            {mutation.isPending ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </DrawerShell>
  );
}

// ---------- Delete confirm ----------

interface ConfirmDeleteProps {
  publication: Publication;
  onCancel: () => void;
  onDeleted: () => void;
}

function ConfirmDeleteModal({
  publication,
  onCancel,
  onDeleted,
}: ConfirmDeleteProps) {
  const mutation = useApiMutation<void, void>(
    `/publications/${publication.id}`,
    "DELETE",
    {
      onSuccess: () => onDeleted(),
    }
  );

  return (
    <ModalShell onClose={onCancel} eyebrow="Publications · danger" title="Delete publication?">
      <p className="settings-confirm-copy">
        Remove <strong>{publication.name}</strong>? This deletes the
        publication and cascades to any pieces, publish targets, and
        amplifiers attached to it. There is no undo. The underlying voice
        profile and brand identity remain.
      </p>
      {mutation.error && (
        <div className="settings-form-error">{mutation.error.message}</div>
      )}
      <div className="settings-form-actions">
        <button type="button" className="btn-ghost" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="btn-primary btn-danger"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
        >
          {mutation.isPending ? "Deleting…" : "Delete publication"}
        </button>
      </div>
    </ModalShell>
  );
}

// NOTE: the local ModalShell / DrawerShell that used to live here have been
// removed — both now come from @/components/SettingsModalShell (imported at the
// top of this file). That shared shell renders the canonical anatomy (eyebrow +
// 24px serif title, bordered close, blurred overlay) and handles Escape/backdrop
// close, so nothing else in this file changes.
